import type { Meeting } from '@clareefai/types';
import { Calendar, CheckCircle, Clock, RefreshCw, Users } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@clareefai/ui';
import {
  completedMeetingsOf,
  requestedDatesFor,
  rescheduledDatesFor,
  upcomingMeetingFor,
  type ProposedDate,
} from './meetingSelectors';
import type { PromoterView } from './types';

type TabType = 'promoters' | 'requested' | 'upcoming' | 'history' | 'rescheduled';

interface MeetingTabsProps {
  promoters: PromoterView[];
  meetings: Meeting[];
  onOpenPromoter: (promoterId: string) => void;
  onValidateDate: (promoterId: string, dateId: string) => void;
}

const BANNER_TONES = {
  blue: 'bg-blue-50 border-blue-200 text-blue-800',
  green: 'bg-green-50 border-green-200 text-green-800',
  gray: 'bg-gray-50 border-gray-200 text-gray-700',
  orange: 'bg-orange-50 border-orange-200 text-orange-800',
} as const;

const InfoBanner: React.FC<{ tone: keyof typeof BANNER_TONES; text: string }> = ({ tone, text }) => (
  <div className={`mb-8 p-4 border rounded-xl ${BANNER_TONES[tone]}`}>
    <p className="text-sm">{text}</p>
  </div>
);

const EmptyTab: React.FC<{ icon: React.ElementType; title: string; subtitle?: string }> = ({ icon: Icon, title, subtitle }) => (
  <div className="text-center py-12 text-gray-500">
    <Icon className="w-16 h-16 mx-auto mb-3 text-gray-300" />
    <p className={subtitle ? 'text-lg font-medium' : undefined}>{title}</p>
    {subtitle && <p className="text-sm">{subtitle}</p>}
  </div>
);

/** Bordered card with the promoter's identity header, wrapping tab-specific content. */
const PromoterGroup: React.FC<{ promoter: PromoterView; children: React.ReactNode }> = ({ promoter, children }) => (
  <div className="border border-gray-200 rounded-lg p-5">
    <div className="flex items-center space-x-3 mb-4">
      <Avatar src={promoter.avatar} name={promoter.name} size="md" />
      <div>
        <h3 className="font-semibold text-gray-900">{promoter.name}</h3>
        <p className="text-sm text-gray-600">{promoter.company}</p>
      </div>
    </div>
    {children}
  </div>
);

const EmptyGroup: React.FC<{ icon: React.ElementType; text: string }> = ({ icon: Icon, text }) => (
  <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
    <Icon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
    <p className="text-sm">{text}</p>
  </div>
);

const DATE_ROW_TONES = {
  orange: { row: 'bg-orange-50 border-orange-200', badge: 'bg-orange-600' },
  blue: { row: 'bg-blue-50 border-blue-200', badge: 'bg-blue-600' },
} as const;

const ProposedDateRow: React.FC<{
  proposal: ProposedDate;
  index: number;
  tone: keyof typeof DATE_ROW_TONES;
  actionLabel: string;
  onAction: () => void;
}> = ({ proposal, index, tone, actionLabel, onAction }) => {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center justify-between border rounded-lg p-4 ${DATE_ROW_TONES[tone].row}`}>
      <div className="flex items-center space-x-3">
        <div className={`w-8 h-8 rounded-full text-white flex items-center justify-center font-bold text-sm ${DATE_ROW_TONES[tone].badge}`}>
          {index + 1}
        </div>
        <div>
          <p className="font-medium text-gray-900">
            {new Date(proposal.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-sm text-gray-600">{t('prospects.at')} {proposal.time}</p>
        </div>
      </div>
      <button
        onClick={onAction}
        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
      >
        {actionLabel}
      </button>
    </div>
  );
};

const PromoterSummaryCard: React.FC<{ promoter: PromoterView; onClick: () => void }> = ({ promoter, onClick }) => {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClick}
      className="group border border-gray-200 rounded-xl p-6 hover:shadow-md hover:border-[#4339A0]/40 hover:bg-[#f5f3ff] active:scale-[0.99] transition-[box-shadow,border-color,background-color,transform] duration-150 cursor-pointer"
    >
      <div className="flex flex-col items-center text-center">
        <div className="relative mb-4">
          <Avatar
            src={promoter.avatar}
            name={promoter.name}
            size="lg"
            className="w-20 h-20 ring-4 ring-white shadow-md group-hover:ring-[#4339A0]/30 transition-[box-shadow] duration-150"
          />
          <div className="absolute inset-0 rounded-full bg-[#4339A0]/10 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <h3 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-[#4339A0] transition-colors">{promoter.name}</h3>
        <p className="text-sm text-gray-600 mb-3">{promoter.company}</p>
        <div className="w-full pt-3 border-t border-gray-200 group-hover:border-[#4339A0] transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">{t('prospects.prospectsCount')}</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums group-hover:text-[#4339A0] transition-colors">
              {promoter.prospectsReferred}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{t('prospects.conversion')}</span>
            <span className="text-sm font-semibold text-green-600 tabular-nums group-hover:text-[#4339A0] transition-colors">
              {promoter.conversionRate}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MeetingTabs: React.FC<MeetingTabsProps> = ({ promoters, meetings, onOpenPromoter, onValidateDate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('promoters');

  const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
    { id: 'promoters', label: t('prospects.tabs.promoters'), icon: Users },
    { id: 'requested', label: t('prospects.tabs.requested'), icon: Calendar },
    { id: 'upcoming', label: t('prospects.tabs.upcoming'), icon: CheckCircle },
    { id: 'history', label: t('prospects.tabs.history'), icon: Clock },
    { id: 'rescheduled', label: t('prospects.tabs.rescheduled'), icon: RefreshCw },
  ];

  const pastMeetings = completedMeetingsOf(meetings);

  return (
    <div className="space-y-6">
      <div className="feel-in bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 overflow-hidden sticky top-0 z-10" style={{ animationDelay: '80ms' }}>
        <div className="flex overflow-x-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-[color,background-color,border-color] duration-150 whitespace-nowrap ${activeTab === id
                  ? 'text-[#4339A0] border-b-2 border-[#4339A0] bg-[#f5f3ff]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
            >
              <Icon className="w-5 h-5" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="feel-in space-y-6" style={{ animationDelay: '160ms' }}>
        {activeTab === 'promoters' && (
          promoters.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <Users className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-medium text-gray-900 mb-2">{t('prospects.noPromotersAssigned')}</p>
              <p className="text-gray-600">{t('prospects.noPromotersAssignedHelp')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {promoters.map((promoter) => (
                <PromoterSummaryCard key={promoter.id} promoter={promoter} onClick={() => onOpenPromoter(promoter.id)} />
              ))}
            </div>
          )
        )}

        {activeTab === 'requested' && (
          <div>
            <InfoBanner tone="blue" text={t('prospects.requestedInfo')} />
            {promoters.length === 0 ? (
              <EmptyTab icon={Calendar} title={t('prospects.noPromotersAssigned')} />
            ) : (
              <div className="space-y-6">
                {promoters.map((promoter) => {
                  const requests = requestedDatesFor(meetings, promoter.id);
                  return (
                    <PromoterGroup key={promoter.id} promoter={promoter}>
                      {requests.length === 0 ? (
                        <EmptyGroup icon={Calendar} text={t('prospects.noDateProposals')} />
                      ) : (
                        <div className="space-y-3">
                          {requests.map((proposal, index) => (
                            <ProposedDateRow
                              key={proposal.id}
                              proposal={proposal}
                              index={index}
                              tone="orange"
                              actionLabel={t('prospects.validate')}
                              onAction={() => onValidateDate(promoter.id, proposal.id)}
                            />
                          ))}
                        </div>
                      )}
                    </PromoterGroup>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'upcoming' && (
          <div>
            <InfoBanner tone="green" text={t('prospects.upcomingInfo')} />
            {promoters.length === 0 ? (
              <EmptyTab icon={CheckCircle} title={t('prospects.noPromotersAssigned')} />
            ) : (
              <div className="space-y-4">
                {promoters.map((promoter) => {
                  const meeting = upcomingMeetingFor(meetings, promoter.id);
                  return (
                    <PromoterGroup key={promoter.id} promoter={promoter}>
                      {!meeting ? (
                        <EmptyGroup icon={CheckCircle} text={t('prospects.noValidatedMeeting')} />
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-900">{meeting.type}</p>
                              <p className="text-sm text-gray-600 mt-1">
                                {new Date(meeting.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} {t('prospects.at')} {meeting.time}
                              </p>
                            </div>
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                              {t('prospects.confirmed')}
                            </span>
                          </div>
                        </div>
                      )}
                    </PromoterGroup>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            <InfoBanner tone="gray" text={t('prospects.historyInfo')} />
            {pastMeetings.length === 0 ? (
              <EmptyTab icon={Clock} title={t('prospects.noMeetingHistory')} subtitle={t('prospects.noMeetingsCompleted')} />
            ) : (
              <div className="space-y-4">
                {pastMeetings.map((meeting) => (
                  <div key={meeting.id} className="border border-gray-200 rounded-lg p-5 bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{meeting.promoterName}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {new Date(meeting.date).toLocaleDateString('en-US')} {t('prospects.at')} {meeting.time}
                        </p>
                        <p className="text-sm text-gray-700 mt-2">{meeting.outcome}</p>
                      </div>
                      <span className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">
                        {t('common.completed')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'rescheduled' && (
          <div>
            <InfoBanner tone="orange" text={t('prospects.rescheduledInfo')} />
            {promoters.length === 0 ? (
              <EmptyTab icon={RefreshCw} title={t('prospects.noPromotersAssigned')} />
            ) : (
              <div className="space-y-6">
                {promoters.map((promoter) => {
                  const rescheduled = rescheduledDatesFor(meetings, promoter.id);
                  return (
                    <PromoterGroup key={promoter.id} promoter={promoter}>
                      {rescheduled.length === 0 ? (
                        <EmptyGroup icon={RefreshCw} text={t('prospects.noRescheduledDates')} />
                      ) : (
                        <div className="space-y-3">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                            <p className="text-sm text-blue-800">{t('prospects.rescheduledNote')}</p>
                          </div>
                          {rescheduled.map((proposal, index) => (
                            <ProposedDateRow
                              key={proposal.id}
                              proposal={proposal}
                              index={index}
                              tone="blue"
                              actionLabel={t('prospects.accept')}
                              onAction={() => onValidateDate(promoter.id, proposal.id)}
                            />
                          ))}
                        </div>
                      )}
                    </PromoterGroup>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingTabs;
